import type { ExpressionSpecification, StyleSpecification } from 'maplibre-gl';

export type Basemap = 'theme' | 'satellite';

/** Resolved value of a design token on <html> (follows the active theme). */
export function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
}

const OFM_ATTRIBUTION =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

const FONT_REGULAR = ['Noto Sans Regular'];
const FONT_BOLD = ['Noto Sans Bold'];

const zoomWidth = (stops: Array<[number, number]>): ExpressionSpecification =>
  ['interpolate', ['exponential', 1.5], ['zoom'], ...stops.flat()] as unknown as ExpressionSpecification;

/**
 * House basemap: OpenFreeMap vector tiles (OpenMapTiles schema) coloured from the
 * FTTH NMS `map-*` tokens, so dark and light match the design system exactly.
 */
export function buildStyle(basemap: Basemap): StyleSpecification {
  const glyphs = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf';

  if (basemap === 'satellite') {
    return {
      version: 8,
      glyphs,
      sources: {
        satellite: {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
          maxzoom: 19,
          attribution: 'Citra © Esri, Maxar, Earthstar Geographics',
        },
      },
      layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
    };
  }

  const land = token('map-land');
  const road = token('map-road');
  const major = token('map-road-major');
  const water = token('map-water');
  const park = token('map-park');
  const label = token('text-muted');
  const placeLabel = token('text-secondary');

  return {
    version: 8,
    glyphs,
    sources: {
      omt: { type: 'vector', url: 'https://tiles.openfreemap.org/planet', attribution: OFM_ATTRIBUTION },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': land } },
      {
        id: 'landcover-green',
        type: 'fill',
        source: 'omt',
        'source-layer': 'landcover',
        filter: ['in', ['get', 'class'], ['literal', ['grass', 'wood', 'farmland', 'wetland']]],
        paint: { 'fill-color': park, 'fill-opacity': 0.7 },
      },
      { id: 'park', type: 'fill', source: 'omt', 'source-layer': 'park', paint: { 'fill-color': park } },
      { id: 'water', type: 'fill', source: 'omt', 'source-layer': 'water', paint: { 'fill-color': water } },
      {
        id: 'waterway',
        type: 'line',
        source: 'omt',
        'source-layer': 'waterway',
        paint: {
          'line-color': water,
          'line-width': zoomWidth([
            [8, 0.5],
            [14, 2],
            [18, 6],
          ]),
        },
      },
      {
        id: 'building',
        type: 'fill',
        source: 'omt',
        'source-layer': 'building',
        minzoom: 14,
        paint: { 'fill-color': road, 'fill-opacity': 0.35 },
      },
      {
        id: 'road-minor',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        filter: ['in', ['get', 'class'], ['literal', ['minor', 'service', 'track', 'path']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': road,
          'line-width': zoomWidth([
            [12, 0.4],
            [15, 2.5],
            [18, 10],
          ]),
        },
      },
      {
        id: 'road-major',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        filter: [
          'in',
          ['get', 'class'],
          ['literal', ['motorway', 'trunk', 'primary', 'secondary', 'tertiary']],
        ],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': major,
          'line-width': zoomWidth([
            [6, 0.6],
            [12, 2],
            [15, 5],
            [18, 16],
          ]),
        },
      },
      {
        id: 'road-label',
        type: 'symbol',
        source: 'omt',
        'source-layer': 'transportation_name',
        minzoom: 14,
        layout: {
          'symbol-placement': 'line',
          'text-field': ['get', 'name'],
          'text-font': FONT_REGULAR,
          'text-size': 10.5,
        },
        paint: { 'text-color': label, 'text-halo-color': land, 'text-halo-width': 1.2 },
      },
      {
        id: 'place-label',
        type: 'symbol',
        source: 'omt',
        'source-layer': 'place',
        filter: ['in', ['get', 'class'], ['literal', ['city', 'town', 'village', 'suburb', 'hamlet']]],
        layout: {
          'text-field': ['coalesce', ['get', 'name:id'], ['get', 'name']],
          'text-font': FONT_BOLD,
          'text-size': ['match', ['get', 'class'], 'city', 14, 'town', 12.5, 11],
          'text-letter-spacing': 0.04,
        },
        paint: { 'text-color': placeLabel, 'text-halo-color': land, 'text-halo-width': 1.4 },
      },
    ],
  };
}

export const MARKER_FONT = FONT_BOLD;
export const LABEL_FONT = FONT_REGULAR;
